import { dispatch } from '@src/dispatcher';
import { GOOGLE_ANALYTICS, PERMISSION_LEVELS } from '@src/const';

import {
  userHasPermission,
  permissionErrorAlert,
  sendGoogleAnalyticsEvents,
} from '@src/actions/_utils';

import { normalizers, validators } from '../utils';
import { ACTION_TYPES, DEFAULTS } from '../const';
import * as Api from './api';
import Store from './store';

const { getStateImmutable } = Store;

export function getNewBusinessData(viaName) {
  return new Promise(async (resolve) => {
    const businessResponse = await Api.getBusinessData(viaName);
    dispatch(ACTION_TYPES.CREATE_NEW_BUSINESS, {
      data: DEFAULTS.BUSINESS,
      meta: {
        ...businessResponse.meta,
      },
    });

    resolve();
  });
}

export async function getBusinessData(viaName) {
  if (viaName === 'new') return getNewBusinessData(viaName);

  return new Promise(async (resolve) => {
    const businessResponse = await Api.getBusinessData(viaName);
    const businessId = businessResponse.body[0].id;
    const [
      triggerResponse,
      deploymentResponse,
      platformResponse,
      defaultTeamResponse,
    ] = await Promise.all([
      Api.getBusinessTriggerSettings(businessId),
      Api.getPlatformDeployments(businessId),
      Api.getAllPlatforms(),
      Api.getBusinessDefaultTeam(businessId),
    ]);

    const data = normalizers.fromBusinessAPI({
      businessResponse,
      triggerResponse,
      deploymentResponse,
      defaultTeamResponse,
    });

    dispatch(ACTION_TYPES.RECEIVED_BUSINESS_DATA, {
      data,
      meta: {
        ...businessResponse.meta,
        allPlatforms: platformResponse.body,
        allTeams: defaultTeamResponse.meta,
        allClients: deploymentResponse.meta.client_list,
      },
    });

    resolve();
  });
}

export function editBusinessData(key, value) {
  const businessData = getStateImmutable().businessData.set(key, value);
  dispatch(ACTION_TYPES.EDITED_BUSINESS_DATA, {
    data: businessData,
  });
}

export function validateBusinessData() {
  return new Promise(async (resolve) => {
    const { businessData } = getStateImmutable();
    const errors = await validators.validateBusinessData(businessData);
    dispatch(ACTION_TYPES.VALIDATED_BUSINESS_DATA, { data: errors });
    resolve(errors);
  });
}

export function clearBusinessDataValidationErrors() {
  dispatch(ACTION_TYPES.VALIDATED_BUSINESS_DATA, { data: [] });
}

export function saveBusinessData({ isNew }) {
  return new Promise(async (resolve, reject) => {
    const {
      businessData,
      partnerIdMap,
      allPlatforms,
      allTeams,
    } = getStateImmutable();

    let updatedBusinessData = { ...businessData };

    // First we check if settings indexed is present (if new business, its not)
    // This computation decides the status of "webhook_enabled" flag for JS-SDK configuration.
    // We've removed it from the UI.
    // If any of platform deployment and it's respective webhook is enabled,
    //  we mark "webhook_enabled" status as true and vice-versa. This happens for every partner.
    if (
      Object.prototype.hasOwnProperty.call(
        businessData.partnerSpecificSettings,
        'settingsIndexed'
      )
    ) {
      let updatedSettingsIndexed = {};

      Object.keys(businessData.partnerSpecificSettings.settingsIndexed).map(
        (key) => {
          let webhookEnabled = false;

          const platformDeployments = {
            ...businessData.partnerSpecificSettings.settingsIndexed[key]
              .platformDeployments,
          };

          Object.keys(platformDeployments).map((key) => {
            if (
              platformDeployments[key].active &&
              platformDeployments[key].webhook_enabled
            )
              webhookEnabled = true;
          });

          const sdkConfiguration = {
            ...businessData.partnerSpecificSettings.settingsIndexed[key]
              .sdkConfiguration,
            webhookEnabled,
          };

          updatedSettingsIndexed = {
            ...updatedSettingsIndexed,
            [key]: {
              ...businessData.partnerSpecificSettings.settingsIndexed[key],
              sdkConfiguration,
            },
          };
        }
      );

      // updates business data after computing the "webhook_enabled" flag.
      updatedBusinessData = {
        ...updatedBusinessData,
        partnerSpecificSettings: {
          ...businessData.partnerSpecificSettings,
          settingsIndexed: { ...updatedSettingsIndexed },
        },
      };
    }

    const businessId = updatedBusinessData.generalSettings.id;
    const data = normalizers.toBusinessAPI(updatedBusinessData, {
      partnerIdMap,
      allPlatforms,
      allTeams,
      businessId,
    });

    let API_CALLS = [Api.saveBusinessData(data.businessData)];
    if (!isNew) {
      API_CALLS = API_CALLS.concat([
        Api.saveBusinessDefaultTeam(businessId, data.defaultTeam),
        Api.savePlatformDeployments(businessId, data.platformDeployments),
        Api.savePromptTriggerData(businessId, {
          bot_prompt_trigger_list: data.botPromptTriggers,
        }),
      ]);
    }

    return Promise.all(API_CALLS).then(resolve).catch(reject);
  });
}

export function createNewBusiness() {
  if (!userHasPermission(PERMISSION_LEVELS.BUSINESS_TASK_EDIT_TRANSFER)) {
    permissionErrorAlert('create new business');
    return;
  }

  dispatch(ACTION_TYPES.CREATE_NEW_BUSINESS, {});
}

export async function transferPromptTriggers(triggerIdList, additionalHeaders) {
  if (
    !userHasPermission(PERMISSION_LEVELS.BUSINESS_PROMPT_TRIGGER_EDIT_TRANSFER)
  ) {
    return permissionErrorAlert('publish bot prompt triggers');
  }

  if (triggerIdList.length === 0) {
    return true;
  }

  const response = Api.transferSQL(
    'bot_prompt_triggers',
    triggerIdList,
    additionalHeaders
  );

  let apiResult;

  await response
    .then((result) => {
      apiResult = result.success;
    })
    .catch(() => {
      apiResult = false;
    });

  return apiResult;
}

export async function transferBusiness(businessId, additionalHeaders) {
  if (!userHasPermission(PERMISSION_LEVELS.BUSINESS_TASK_EDIT_TRANSFER)) {
    return permissionErrorAlert('publish business');
  }
  const response = Api.transferSQL('business', [businessId], additionalHeaders);

  let apiResult;

  await response
    .then((result) => {
      sendGoogleAnalyticsEvents(
        GOOGLE_ANALYTICS.CATEGORY.TRANSFER,
        GOOGLE_ANALYTICS.EVENTS.BUSINESS_TRANSFER,
        `business-${businessId}`,
        GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
      );
      apiResult = result.success;
    })
    .catch(() => {
      apiResult = false;
    });

  return apiResult;
}

export async function transferTasks(taskIdList, additionalHeaders) {
  if (!userHasPermission(PERMISSION_LEVELS.BUSINESS_TASK_EDIT_TRANSFER)) {
    return permissionErrorAlert('publish business');
  }

  if (taskIdList.length === 0) {
    return true;
  }

  const response = Api.transferSQL('task', taskIdList, additionalHeaders);

  let apiResult;

  await response
    .then((result) => {
      sendGoogleAnalyticsEvents(
        GOOGLE_ANALYTICS.CATEGORY.TRANSFER,
        GOOGLE_ANALYTICS.EVENTS.TASK_TRANSFER,
        `tasks-${taskIdList}`,
        GOOGLE_ANALYTICS.VALUE.SINGLE_CLICK
      );

      apiResult = result.success;
    })
    .catch(() => {
      apiResult = false;
    });

  return apiResult;
}
